// frontend/web/src/audio/buses.js
//
// SFX / ambient / voice bus wiring for AudioBank.
//
// Phase 2 keeps this intentionally minimal: three GainNodes connected
// straight to destination. Ducking, compression, side-chaining etc. are
// deferred — Phase 4+ will add a typewriter-ducked voice bus on top of
// this same factory.
//
// Phase 2 follow-up adds an optional `levels` parameter so the initial
// bus gains come from `audio/levels.json` instead of hardcoded defaults.
// The signature stays backwards-compatible: callers that don't pass
// `levels` get the same defaults as before.
//
// Exports:
//   createBuses(ctx, levels?) -> { sfx, ambient, voice }
//
// Each returned node is a GainNode. Effects/voice playback connect their
// per-cue gain into the appropriate bus node; the bus controls global
// volume for that channel. The `levels` argument shape matches
// `frontend/web/src/audio/levels.json`:
//
//   {
//     "buses": {
//       "voice":   { "gain": 1.00 },
//       "sfx":     { "gain": 2.80 },
//       "ambient": { "gain": 0.70 }
//     },
//     "master": 1.00
//   }
//
// Levels are a hot-reloadable primitive — `AudioBank.applyLevels()` will
// rewrite GainNode.gain.value on each existing bus when the preview
// harness moves a slider. This factory is only responsible for the
// initial values at construction time.

// ── MASTER IS A REAL NODE, NOT A MULTIPLIER FOLDED INTO EACH BUS ──────────
//
// This used to compute `bus.gain = master * busGain` and connect each bus
// straight to destination. That is arithmetically the same signal, and it was
// fine while nothing needed to observe or move the sum -- but it left `master`
// with no node of its own, so there was nowhere to hang a meter and no single
// point where the mix could be limited.
//
// Now: each bus carries ONLY its own gain and feeds a master GainNode, which
// carries `levels.master` and feeds destination. The product reaching the
// speakers is unchanged (master x busGain, same as before); what changes is
// that the sum now exists as an addressable node.
//
// ⚠️ `applyLevels()` MUST NOT re-multiply master into the bus gains now that
// this node exists, or master is applied twice. `bank.js` sets the buses and
// the master node separately, and `buses-master-staging.test.js` pins the
// product against the old folded math so the equivalence cannot silently drift.
export function createBuses(ctx, levels = null) {
  if (!ctx) {
    throw new Error('[audio/buses] createBuses requires an AudioContext');
  }

  const sfx = ctx.createGain();
  const ambient = ctx.createGain();
  const voice = ctx.createGain();
  const master = ctx.createGain();

  if (levels && levels.buses) {
    const masterGain = typeof levels.master === 'number' ? levels.master : 1;
    const sfxGain = levels.buses.sfx && typeof levels.buses.sfx.gain === 'number'
      ? levels.buses.sfx.gain : 1;
    const ambientGain = levels.buses.ambient && typeof levels.buses.ambient.gain === 'number'
      ? levels.buses.ambient.gain : 0.7;
    const voiceGain = levels.buses.voice && typeof levels.buses.voice.gain === 'number'
      ? levels.buses.voice.gain : 1;

    sfx.gain.value = sfxGain;
    ambient.gain.value = ambientGain;
    voice.gain.value = voiceGain;
    master.gain.value = masterGain;
  } else {
    // Sensible defaults — preview harness and live game can tweak later.
    sfx.gain.value = 1.0;
    ambient.gain.value = 0.7;
    voice.gain.value = 1.0;
    master.gain.value = 1.0;
  }

  sfx.connect(master);
  ambient.connect(master);
  voice.connect(master);

  // ── LIMITER ───────────────────────────────────────────────────────────
  // The mix had NO headroom and nothing catching it: with sfx at 2.8, most
  // cues exceed unity on their own, and cues that fire together sum on top of
  // that -- a max face is `die.settle` + `die.ceiling` in the SAME INSTANT for
  // ~3.77. Past 1.0 the platform clamps, which is distortion, and distortion
  // is not a level. Tuning a cue down because it "sounds harsh" when the
  // harshness is the sum clamping tunes the wrong parameter.
  //
  // ⚠️ THIS IS A SAFETY NET, NOT A MIX DECISION, AND IT MUST NOT HIDE THE
  // PROBLEM. The lab's meter is tapped on `master` -- BEFORE this node -- so
  // the pre-limiter overshoot stays VISIBLE, and `reduction` is displayed
  // beside it. A limiter that silently absorbed the overshoot would train
  // everyone to push levels further into it, which is how a mix ends up
  // permanently compressed and nobody knows why. Re-normalising the buses so
  // the limiter rarely engages is a LISTENING decision and is deliberately
  // NOT made here. [2026-09-01-001-ENGINE]
  //
  // Settings are limiter-shaped rather than compressor-shaped: a hard ratio
  // just below unity, a knee of 0 so nothing below threshold is touched at
  // all, and a fast attack so a die transient is caught rather than passed.
  let limiter = null;
  try {
    limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -1.0;  // dB — engage just under full scale
    limiter.knee.value = 0;          // hard: quiet material is untouched
    limiter.ratio.value = 20;        // limiting, not compression
    limiter.attack.value = 0.003;    // catch a die settle
    limiter.release.value = 0.10;
    master.connect(limiter);
    limiter.connect(ctx.destination);
  } catch (_err) {
    // A platform without DynamicsCompressor still gets audio, unlimited.
    // Louder and clipping beats silent, and the meter still tells the truth.
    limiter = null;
    master.connect(ctx.destination);
  }

  return { sfx, ambient, voice, master, limiter };
}
